import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Clock, TrendingUp, TrendingDown, Minus, Share2, LineChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import TimeMachineShareModal from './TimeMachineShareModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WatchlistTimeMachineBar
 * ------------------------
 * "If you'd bought $1000 of TICKER 1 year ago with DRIP on, here's
 * where you'd be today" — orange-outlined info bar that appears under
 * each watchlist row when the Time Machine toggle is ON.
 *
 * Lazy-loads its own data on mount via /api/watchlist/time-machine/{ticker},
 * so the parent (Dashboard) doesn't need to pre-fetch all rows. The
 * backend caches per-ticker results for 24h so repeat visits are instant.
 *
 * Renders:
 *   • Current value of $1000 + total return %
 *   • DRIP stats: shares now, dividends received, YOC
 *   • Annual income now, portfolio yield on invested
 *   • Three benchmark deltas: vs SPY / SCHD / VYM
 *   • Verdict pill: Great Buy / Good Buy / Lukewarm / Underperformed / Bad Buy
 *
 * Props:
 *   • ticker — the stock symbol (e.g. "KO")
 */
const VERDICT_META = {
  great_buy: {
    label: 'Great Buy',
    cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 ring-emerald-400/40',
    icon: TrendingUp,
    tip: 'Beat SPY by 5%+ AND made money. Your $1,000 is dramatically better off here than in the index — strong absolute and relative result.',
  },
  good_buy: {
    label: 'Good Buy',
    cls: 'bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300 ring-green-400/40',
    icon: TrendingUp,
    tip: 'Made money AND matched or beat SPY. Solid pick — you came out ahead vs simply parking the cash in the broad market.',
  },
  lukewarm: {
    label: 'Lukewarm',
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 ring-amber-400/40',
    icon: Minus,
    tip: 'Made money but lagged SPY by less than 5%. Decent absolute return — the index would have been a bit better.',
  },
  underperformed: {
    label: 'Underperformed',
    cls: 'bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300 ring-orange-400/40',
    icon: TrendingDown,
    tip: 'Made money but lagged SPY by 5% or more. The S&P 500 would have been notably better — worth checking whether the dividend story still justifies the spot.',
  },
  bad_buy: {
    label: 'Bad Buy',
    cls: 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 ring-rose-400/40',
    icon: TrendingDown,
    tip: 'Lost money over the past year — your $1,000 (with DRIP on) is now worth less than $1,000. Even if dividends came in, the price drop more than offset them.',
  },
  unknown: {
    label: 'Unknown',
    cls: 'bg-muted text-muted-foreground ring-border/40',
    icon: Minus,
    tip: 'Not enough data to assign a verdict (typically because SPY benchmark data was unavailable for the period).',
  },
};

function Stat({ label, value, valueCls = '', labelCls = '', subCls = '', title, sub, testid }) {
  return (
    <div className="flex flex-col" title={title} data-testid={testid}>
      <span className={`text-[9px] uppercase tracking-wider text-muted-foreground font-bold ${labelCls}`}>{label}</span>
      <span className={`text-xs font-mono font-bold ${valueCls}`}>{value}</span>
      {sub && <span className={`text-[10px] text-muted-foreground font-mono ${subCls}`}>{sub}</span>}
    </div>
  );
}

function BenchmarkDelta({ name, stockReturn, benchmarkReturn, familyLabel }) {
  if (benchmarkReturn == null || stockReturn == null) {
    return <Stat label={`vs ${name}`} value="—" valueCls="text-muted-foreground" labelCls="text-[10px]" title={`No data available for the ${name} benchmark over this period.`} />;
  }
  const delta = stockReturn - benchmarkReturn;
  const pos = delta >= 0;
  const blurb = { SPY: 'SPY tracks the S&P 500 — the broad U.S. market benchmark.', SCHD: 'SCHD is the Schwab U.S. Dividend Equity ETF — the most popular benchmark for quality dividend strategies.', VYM: 'VYM is the Vanguard High Dividend Yield ETF — a broad high-yield benchmark.' }[name]
    || (familyLabel ? `${name} is one of the ${familyLabel} benchmarks — the closest like-for-like comparison for this fund.` : '');
  const tip = `${name} returned ${benchmarkReturn >= 0 ? '+' : ''}${benchmarkReturn.toFixed(2)}% over the same window (price + dividends, with DRIP). This stock returned ${stockReturn >= 0 ? '+' : ''}${stockReturn.toFixed(2)}%, ${pos ? `beating ${name} by ${Math.abs(delta).toFixed(2)}%.` : `lagging ${name} by ${Math.abs(delta).toFixed(2)}%.`} ${blurb}`;
  return (
    <Stat
      label={`vs ${name}`}
      value={`${pos ? '+' : ''}${delta.toFixed(1)}%`}
      valueCls={pos ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}
      labelCls="text-[10px]"
      title={tip}
    />
  );
}

export default function WatchlistTimeMachineBar({ ticker }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  // Feb 2026 — Track the actual mobile viewport width so we can hard-
  // pin the card's rendered width in pixels. The `100vw` CSS calc
  // was misbehaving inside the horizontally-scrollable stock table
  // (the wide table pushed the layout viewport beyond the visual
  // viewport on some mobile browsers), causing this card to extend
  // past the phone screen's right edge. We now measure the card's
  // actual left offset via a ref and set width = visibleViewport -
  // actualLeft - 12px so the right edge always lands safely inside
  // the phone screen regardless of parent padding.
  const cardRef = useRef(null);
  const [mobileWidth, setMobileWidth] = useState(null);
  useEffect(() => {
    const measure = () => {
      const vw = document.documentElement.clientWidth || window.innerWidth || 0;
      if (vw >= 640) { setMobileWidth(null); return; }
      if (!cardRef.current) return;
      // Temporarily neutralize any width we set so we can read the
      // natural left offset. Otherwise the previously-set width would
      // bias our measurement.
      const prev = cardRef.current.style.width;
      cardRef.current.style.width = 'auto';
      const rect = cardRef.current.getBoundingClientRect();
      cardRef.current.style.width = prev;
      const safeRight = 24;
      const w = Math.max(200, Math.floor(vw - rect.left - safeRight));
      setMobileWidth(w);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [data]);
  // Tracks whether the component is still mounted. We only flip this
  // false on actual unmount, NOT on effect-cleanup runs caused by
  // React 18 StrictMode double-invocation or parent re-renders.
  // Without this, the StrictMode cleanup of the first effect would
  // set cancelled=true while fetchedRef blocks the second effect from
  // restarting — so a successfully-returned 200 OK fetch silently
  // dropped its setData/setLoading calls and the bar was stuck on
  // "Crunching…" forever.
  const mountedRef = useRef(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Re-arm mounted flag on every (re-)mount.
    mountedRef.current = true;
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/watchlist/time-machine/${encodeURIComponent(ticker)}`);
        if (mountedRef.current) setData(res.data);
      } catch (err) {
        if (mountedRef.current) setError(err?.response?.data?.detail || err?.message || 'Could not load');
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();
    // No cleanup that touches state — that's what caused the stuck-loading
    // bug. The component-unmount cleanup below (separate effect) is the
    // ONLY place we flip mountedRef false.
  }, [ticker]);

  useEffect(() => () => { mountedRef.current = false; }, []);

  if (loading) {
    return (
      <div
        className="mx-3 sm:mx-6 mt-1 mb-2 px-3 py-2 rounded-lg border-2 border-orange-300/70 dark:border-orange-700/70 bg-orange-50/40 dark:bg-orange-950/20"
        data-testid={`time-machine-bar-${ticker}-loading`}
      >
        <div className="flex items-center gap-2 text-[11px] text-orange-700 dark:text-orange-300">
          <Clock className="w-3 h-3 animate-pulse" />
          Crunching 1-year DRIP simulation for {ticker}…
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div
        className="mx-3 sm:mx-6 mt-1 mb-2 px-3 py-2 rounded-lg border-2 border-orange-300/70 dark:border-orange-700/70 bg-orange-50/40 dark:bg-orange-950/20 text-[11px] text-muted-foreground"
        data-testid={`time-machine-bar-${ticker}-error`}
      >
        Time Machine not available for {ticker}: {error || 'no data'}
      </div>
    );
  }

  const verdict = VERDICT_META[data.verdict] || VERDICT_META.unknown;
  const VerdictIcon = verdict.icon;
  const periodMonths = data.fallback_period_months || 12;
  const periodLabel = periodMonths === 12 ? '1 year ago' : `${periodMonths} months ago`;
  const totalReturn = data.total_return_pct ?? 0;
  const benchTickers = (Array.isArray(data.benchmark_tickers) && data.benchmark_tickers.length ? data.benchmark_tickers : ['SPY', 'SCHD', 'VYM']).slice(0, 3);
  const isProfit = totalReturn >= 0;
  const sharesGained = (data.current_shares || 0) - (data.initial_shares || 0);
  // Total $ reinvested via DRIP equals the cash dividends received,
  // since each cash payout was immediately rebought at that day's
  // closing price (see _simulate_drip_one in server.py).
  const dripReinvested = data.total_dividends_received || 0;

  return (
    <div
      ref={cardRef}
      /* Feb 2026 — Mobile: the parent StockTable is horizontally
         scrollable (overflow-x-auto) with a total table width wider
         than the phone viewport. Without help, this bar inherits the
         oversized td width and forces the user to scroll left/right
         to read it. Fix: pin it sticky at left:12px with a viewport-
         relative width so the bar always sits inside the visible
         screen area regardless of column scroll. Desktop (sm+) resets
         to the original mx-6 layout. */
      className="mt-1 mb-2 px-3 py-2 rounded-lg border-2 border-orange-300/70 dark:border-orange-700/70 bg-orange-50/40 dark:bg-orange-950/20 overflow-hidden sticky left-3 sm:static sm:left-auto sm:w-auto sm:max-w-none sm:mx-6"
      style={mobileWidth ? { width: `${mobileWidth}px`, maxWidth: `${mobileWidth}px` } : undefined}
      data-testid={`time-machine-bar-${ticker}`}
    >
      {/* Header row — orange Clock icon + the headline outcome + verdict pill.
          Feb 2026 — On mobile, the "$1,000 in TICKER → $..." headline
          drops to a new line UNDER "Time Machine · 1 year ago" so the
          whole card can shrink to the viewport width without wrapping
          mid-phrase. The verdict pill (Great Buy / Good Buy / etc.)
          rides along with the headline on that second row so the top
          row stays a tight "Time Machine chip ← ... → Share" pair.
          Desktop keeps the original single-row layout with verdict on
          the right. */}
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Clock className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400 shrink-0" />
          <span className="text-[10px] uppercase tracking-wider text-orange-700 dark:text-orange-400 font-bold">
            Time Machine · {periodLabel}
          </span>
          <span className="text-[11px] text-foreground hidden sm:inline">
            $1,000 in <span className="font-mono font-bold">{ticker}</span> →
            <span className={`font-mono font-bold ml-1 ${isProfit ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              ${data.current_value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </span>
            <span className={`ml-1 text-[10px] font-mono ${isProfit ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              ({isProfit ? '+' : ''}{totalReturn.toFixed(2)}%)
            </span>
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 cursor-help ${verdict.cls}`}
            title={verdict.tip}
            data-testid={`time-machine-verdict-${ticker}`}
          >
            <VerdictIcon className="w-3 h-3" />
            {verdict.label}
          </span>
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 ring-cyan-400/40 bg-cyan-100 text-cyan-700 dark:bg-cyan-950/60 dark:text-cyan-300 hover:bg-cyan-200 dark:hover:bg-cyan-950/80 transition-colors"
            title={`Share this Time Machine result (${ticker} 1-year DRIP) as a branded PNG card — perfect for posting on Reddit / X.`}
            data-testid={`time-machine-share-btn-${ticker}`}
          >
            <Share2 className="w-3 h-3" />
            Share
          </button>
        </div>
      </div>
      {/* Mobile-only verdict pill — sits on its own line directly under
          the "Time Machine · 1 year ago" header text per user spec. */}
      <div className="sm:hidden -mt-1 mb-1">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 shrink-0 ${verdict.cls}`}
          title={verdict.tip}
          data-testid={`time-machine-verdict-mobile-${ticker}`}
        >
          <VerdictIcon className="w-3 h-3" />
          {verdict.label}
        </span>
      </div>
      {/* Mobile-only headline. */}
      <div className="sm:hidden mb-2 text-[11px] text-foreground break-words">
        $1,000 in <span className="font-mono font-bold">{ticker}</span> →
        <span className={`font-mono font-bold ml-1 ${isProfit ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
          ${data.current_value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </span>
        <span className={`ml-1 text-[10px] font-mono ${isProfit ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
          ({isProfit ? '+' : ''}{totalReturn.toFixed(2)}%)
        </span>
      </div>

      {/* Main stats grid — 2 cols on phone, 4 on tablet, 7 on desktop.
          Order is read-left-to-right: where you started → what DRIP
          did → what the income looks like now. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
        <Stat
          label="Current Price"
          value={`$${data.current_price.toFixed(2)}`}
          title={`Current closing price for ${ticker} (latest available trading day).`}
          testid={`tm-current-price-${ticker}`}
        />
        <Stat
          label="Shares Bought"
          value={data.initial_shares.toFixed(4)}
          sub={`@ $${data.entry_price.toFixed(2)}`}
          title={`What $1,000 bought ${periodLabel}: ${data.initial_shares.toFixed(4)} shares of ${ticker} at the closing price of $${data.entry_price.toFixed(2)} on ${data.entry_date}.`}
          testid={`tm-shares-bought-${ticker}`}
        />
        <Stat
          label="Shares Now"
          value={data.current_shares.toFixed(4)}
          sub={sharesGained > 0 ? `+${sharesGained.toFixed(4)} from DRIP` : null}
          valueCls={data.drip_buys > 0 ? 'text-emerald-600 dark:text-emerald-400' : ''}
          title={`Total share count today after ${data.drip_buys} DRIP reinvestment${data.drip_buys === 1 ? '' : 's'}. The dividends paid out by ${ticker} over the past year were each rebought at that day's closing price, growing your share count from ${data.initial_shares.toFixed(4)} → ${data.current_shares.toFixed(4)}.`}
          testid={`tm-shares-now-${ticker}`}
        />
        <Stat
          label="DRIP Buys"
          value={`${data.drip_buys} · $${dripReinvested.toFixed(2)}`}
          valueCls={data.drip_buys > 0 ? 'text-emerald-600 dark:text-emerald-400' : ''}
          title={`Number of dividend payouts received over the period and the total dollars reinvested via DRIP. Each cash dividend was immediately rebought at that day's closing price. ${data.drip_buys === 0 ? `${ticker} did not pay any dividends in the window.` : `Total reinvested: $${dripReinvested.toFixed(2)}.`}`}
          testid={`tm-drip-buys-${ticker}`}
        />
        <Stat
          label="Cash Dividends"
          value={`$${dripReinvested.toFixed(2)}`}
          title={`Total cash dividends paid out by ${ticker} on your $1,000 stake over the past year. With DRIP on, every dollar of this was immediately rebought into more shares (which is why "Shares Now" is higher than "Shares Bought").`}
          testid={`tm-cash-divs-${ticker}`}
        />
        <Stat
          label="Forward Income"
          value={data.annual_dividend_income != null ? `$${data.annual_dividend_income.toFixed(2)}/yr` : '—'}
          title={`Forward 12-month dividend income at ${ticker}'s current annual dividend rate × your current ${data.current_shares.toFixed(4)} shares. This is what your $1,000 (now $${data.current_value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}) is on track to pay you over the NEXT year, assuming the dividend rate stays flat.`}
          testid={`tm-forward-income-${ticker}`}
        />
        <Stat
          label="Yield on Cost"
          value={data.yoc != null ? `${data.yoc.toFixed(2)}%` : '—'}
          title={`Effective yield on your original $1,000. Calculated as ${ticker}'s current annual dividend rate divided by the $${data.entry_price.toFixed(2)} entry price. YoC grows over time as the company raises its dividend, even though the headline "yield" reported on the stock is calculated against today's price.`}
          testid={`tm-yoc-${ticker}`}
        />
      </div>

      {/* Benchmark row — the trio the backend picked for this ticker
          (SPY · SCHD · VYM for U.S. names; a like-for-like family such as
          EFA · IEFA · SPDW for international funds). Sep 2026: this used
          to hardcode SPY/SCHD/VYM, so international tickers showed "—". */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-2 pt-1.5 border-t border-orange-200/60 dark:border-orange-800/40">
        {benchTickers.map((b) => (
          <BenchmarkDelta key={b} name={b} stockReturn={totalReturn} benchmarkReturn={data.benchmarks?.[b]} familyLabel={data.benchmark_family_label} />
        ))}
      </div>
      {data.benchmark_family_label && (
        <p className="text-[10px] text-muted-foreground mt-1" data-testid={`tm-benchmark-family-${ticker}`}>
          Benchmarks: {data.benchmark_family_label} — the closest like-for-like set for this fund instead of U.S.-only SPY / SCHD / VYM.
        </p>
      )}

      {/* Entry context footer — anchors the whole card with the
          starting trade. Feb 2026: `break-words` + `whitespace-normal`
          so the long "Entry: bought ... sh @ $... on 2025-07-14 (latest
          data: 2026-07-31)" line wraps cleanly on phone widths instead
          of forcing horizontal overflow. */}
      <div className="text-[11px] text-muted-foreground mt-1.5 pt-1 border-t border-orange-200/40 dark:border-orange-800/30 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="break-words whitespace-normal min-w-0">
          Entry: bought {data.initial_shares.toFixed(4)} sh @ ${data.entry_price.toFixed(2)} on {data.entry_date} (latest data: {data.current_date})
        </span>
        <Link
          to={`/true-return/${[ticker, ...benchTickers].map((t) => t.toLowerCase()).join('-vs-')}?y=1`}
          state={{ scrollToChart: true }}
          className="inline-flex items-center gap-1 font-semibold text-sky-700 dark:text-sky-300 hover:underline underline-offset-2 whitespace-nowrap shrink-0"
          title={`Open the True Return Chart: $10,000 in ${ticker} vs ${benchTickers.join(', ')} — inflation-adjusted, DRIP on/off, 1 to 5 years.`}
          data-testid={`tm-true-return-link-${ticker}`}
        >
          <LineChart className="w-3 h-3" />
          View in True Return Chart
        </Link>
      </div>

      <TimeMachineShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        payload={{ ...data, ticker }}
      />
    </div>
  );
}
